import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import getClassNames from 'src/common/utils/getClassNames';
import {
  buildImageSrc, buildSrcSets, ImageParams, SrcSetParam,
} from 'src/common/utils/imageHelpers';

import styles from './Img.module.scss';
import ErrorBoundary from '../ErrorBoundary';

const noop = () => { };

export type ImgProps = {
  src: string;
  alt: string;
  loading?: 'eager' | 'lazy';
  fetchpriority?: 'high' | 'low';
  className?: string;
  srcSetParams?: SrcSetParam[];
  imageParams?: ImageParams;
  width: number;
  height: number;
  withPlaceholder?: boolean;
  withRoundedCornersSm?: boolean;
  withRoundedCornersLg?: boolean;
  withIntrinsicSizing?: boolean;
  /** Reset the failed image state when the src changes */
  resetFailedStateOnUpdate?: boolean;
  /**
   * Specifies if the image should have a fixed ratio
   * or have variable ratios determined per srcset.
   *
   * - `fixed` uses the padding-bottom aspect ratio hack.
   * - `variable` uses the intrinsic dimension of each srcset image.
   */
  variant?: 'fixed' | 'variable';
  onLoad?: () => void;
  fullScreen?: boolean;
};

const Img: FC<ImgProps> = ({
  src,
  alt,
  loading = 'lazy',
  fetchpriority = null,
  className,
  srcSetParams,
  imageParams = {},
  width,
  height,
  withPlaceholder = false,
  withRoundedCornersSm = false,
  withRoundedCornersLg = false,
  withIntrinsicSizing = false,
  resetFailedStateOnUpdate = false,
  variant = 'fixed',
  onLoad = noop,
  fullScreen,
}) => {
  // Treat empty src as a failed load so placeholder shows
  const isEmptySrc = !src || src.trim() === '';
  const [loadingFailed, setLoadingFailed] = useState(isEmptySrc);

  useEffect(() => {
    if (resetFailedStateOnUpdate) {
      setLoadingFailed(false);
    }
    if (isEmptySrc) {
      setLoadingFailed(true);
    }
  }, [src, isEmptySrc, resetFailedStateOnUpdate]);

  const imgSrc = buildImageSrc(src, imageParams);
  let img = (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={imgSrc}
      alt={alt}
      loading={loading}
      /* eslint-disable react/no-unknown-property */
      /* @ts-expect-error fetchpriority is new and hasn't been added to the HTMLImageElement type */
      fetchpriority={fetchpriority}
      /* eslint-enable react/no-unknown-property */
      width={width}
      height={height}
      className={classNames(className, getClassNames(
        styles,
        'image',
        classNames({
          fullScreen,
          withIntrinsicSizing,
          withRoundedCornersSm,
          withRoundedCornersLg,
          fixed: variant === 'fixed',
        }),
      ))}
      onError={() => setLoadingFailed(true)}
      onLoad={onLoad}
    />
  );

  if (src && srcSetParams) {
    const srcSets = buildSrcSets(src, srcSetParams, imageParams);

    img = (
      <>
        {srcSets.map((srcSet, i) => (
          <source
            key={`${srcSetParams[i].w}${srcSetParams[i].query}`}
            srcSet={srcSet}
            media={srcSetParams[i].query}
            width={srcSetParams[i].w}
            height={srcSetParams[i].h}
          />
        ))}
        {img}
      </>
    );
  }

  // When showing placeholder, maintain aspect ratio even for variable variant
  // to ensure the placeholder background is visible
  const shouldUseFixedRatio = variant === 'fixed' || (loadingFailed && withPlaceholder);

  return (
    <ErrorBoundary fallbackUI={<div>An unexpected error occurred</div>}>
      <picture
        className={classNames(className, getClassNames(
          styles,
          'root',
          classNames({
            fullScreen,
            withPlaceholder,
            withRoundedCornersSm,
            withRoundedCornersLg,
            withIntrinsicSizing,
            fixed: shouldUseFixedRatio,
          }),
        ))}
        style={{
          paddingBottom: shouldUseFixedRatio ? `${(height / width) * 100}%` : 0,
        }}
      >
        {!loadingFailed && img}
      </picture>
    </ErrorBoundary>
  );
};

export default Img;
